<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Value } from '@sinclair/typebox/value'
import papaparse from 'papaparse'

import {
  WarningOutlined,
  SyncOutlined,
  RedoOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons-vue'
import { type UploadChangeParam, type UploadProps } from 'ant-design-vue'

import {
  type LinkedFarmInvitationRecipientCreateT,
  LinkedFarmInvitationRecipientCreateTB,
  type LinkedFarmInvitationsCreateDTOT,
} from '@ruminati/types_v2/linked_farm_invitation'
import { type VisionSharingScopesT } from '@ruminati/types_v2/vision'

import InfoCard from '@/components/Cards/Shared/InfoCard.vue'

import { useOrganisation } from '@/composables/organisations'
import { useLinkedFarmInvitationsWithLinkedFarms } from '@/composables/linkedFarmInvitations'
import { visionSharingScopeLabels } from '@/utilities/vision'

const props = defineProps<{
  visible: boolean
  onCancel: () => void
  onComplete: () => void
}>()

const { currentOrg } = useOrganisation()
const { createInvitations, isCreatingInvitations, invitations } =
  useLinkedFarmInvitationsWithLinkedFarms()

const useClientReferenceIds = computed(() => {
  return (
    currentOrg.value?.preferences?.visionOrg &&
    currentOrg.value?.preferences.visionOrg.useClientReferenceId
  )
})

const sampleFilePath = computed(() => {
  if (useClientReferenceIds.value) return '/invites_sample_with_id.csv'
  return '/invites_sample.csv'
})

const fileList = ref([] as UploadProps['fileList'])
const uploadError = ref<undefined | string>(undefined)

const recipient = ref<Partial<LinkedFarmInvitationRecipientCreateT>>({
  email: undefined,
  clientReferenceId: undefined,
})
const recipients = ref<LinkedFarmInvitationRecipientCreateT[]>([])

const sendInviteEmail = ref(true)
const invalidEmails = ref<string[]>([])

const availableSharingScopes = computed<Array<VisionSharingScopesT>>(() => {
  return currentOrg.value?.preferences?.visionOrg?.availableSharingScopes ?? []
})

const requestedScopes = ref<Array<VisionSharingScopesT>>([])

watch(
  availableSharingScopes,
  (scopes) => {
    requestedScopes.value = scopes.slice()
  },
  { immediate: true },
)

const requestedScopeOptions = computed(() => {
  return availableSharingScopes.value.map((scope) => {
    return {
      value: scope,
      label: visionSharingScopeLabels[scope],
    }
  })
})

const canSendInvites = computed(() => {
  return recipients.value.length > 0 && requestedScopes.value.length > 0
})

function addIndividualRecipient() {
  const newRecipient: Partial<LinkedFarmInvitationRecipientCreateT> = {
    email: recipient.value.email,
    requestedScopes: requestedScopes.value.slice(),
    ...(recipient.value.clientReferenceId
      ? { clientReferenceId: recipient.value.clientReferenceId }
      : {}),
  }
  const result = Value.Check(LinkedFarmInvitationRecipientCreateTB, newRecipient)
  if (result) {
    if (isEmailExistingInList(newRecipient.email as string)) {
      invalidEmails.value.push(newRecipient.email as string)
    } else {
      recipients.value.push(newRecipient as LinkedFarmInvitationRecipientCreateT)
    }
    recipient.value.email = undefined
    recipient.value.clientReferenceId = undefined
  }
}

function removeRecipient(email: string) {
  const index = recipients.value.findIndex((i) => i.email === email)
  if (index > -1) {
    recipients.value.splice(index, 1)
  }
}

function isEmailExistingInList(newEmail: string) {
  return (
    invitations.value?.find((i) => i.invite.email === newEmail) !== undefined ||
    recipients.value.find((i) => i.email === newEmail) !== undefined
  )
}

function retryUpload() {
  if (fileList.value && fileList.value.length > 0) fileList.value.length = 0
  uploadError.value = undefined
}

function handleUpload(info: UploadChangeParam) {
  papaparse.parse(info.file as any as papaparse.LocalFile, {
    header: true,
    complete: (results: any) => {
      if (results.data.length > 200) {
        uploadError.value = 'CSV contained more than 200 addresses.'
        return
      }
      const emailField = results.meta.fields.find((f: string) => f.toLowerCase() === 'email')
      const referenceField = results.meta.fields.find(
        (f: string) => f.toLowerCase() === 'client_reference_id',
      )
      if (emailField === undefined) {
        uploadError.value =
          'Could not find any data in an "email" column, please check the formatting of your CSV.'
        return
      }
      results.data.forEach((d: any) => {
        const email = d[emailField]
        if (email && email.length > 0) {
          const uploadedRecipient: LinkedFarmInvitationRecipientCreateT = {
            email,
            requestedScopes: requestedScopes.value.slice(),
            ...(referenceField !== undefined && d[referenceField]
              ? { clientReferenceId: d[referenceField] as string }
              : {}),
          }
          if (
            isEmailExistingInList(email) ||
            !Value.Check(LinkedFarmInvitationRecipientCreateTB, uploadedRecipient)
          ) {
            invalidEmails.value.push(email)
          } else {
            recipients.value.push(uploadedRecipient)
          }
        }
      })
    },
  })
}

async function submitNewInvites() {
  if (!currentOrg.value) return
  const body: LinkedFarmInvitationsCreateDTOT = {
    visionOrgId: currentOrg.value.id,
    recipients: recipients.value.map((existingRecipient) => {
      return {
        ...existingRecipient,
        requestedScopes: requestedScopes.value.slice(),
      }
    }),
    sendInviteEmail: sendInviteEmail.value,
  }

  await createInvitations(body)
  props.onComplete()
}
</script>

<template>
  <a-modal
    title="Invite Organisations to Share"
    :destroy-on-close="true"
    :width="800"
    :open="visible"
    :footer="null"
    @cancel="onCancel"
  >
    <a-flex>
      <a-flex vertical class="w-full">
        <InfoCard
          class="mb-4"
          description="Use the field below to enter an email address. Click Add or Enter to add another email address."
        />

        <a-flex data-testid="form-create-invite" class="w-full gap-x-4">
          <a-flex vertical flex="1 0 auto">
            <label class="w-full">
              <a-input
                v-model:value="recipient.email"
                placeholder="Email address"
                autocomplete="off"
                @keyup.enter="addIndividualRecipient"
              />
            </label>
          </a-flex>
          <a-flex vertical flex="0 0 140px">
            <a-button @click="addIndividualRecipient"> Add </a-button>
          </a-flex>
        </a-flex>

        <a-divider style="font-size: 0.8rem"> OR </a-divider>
        <a-flex justify="center">
          <a-flex v-if="fileList && fileList.length === 0" vertical align="center">
            <a-upload
              v-model:file-list="fileList"
              name="file"
              accept=".csv"
              :max-count="1"
              :before-upload="() => false"
              @change="handleUpload"
            >
              <a-button>
                <upload-outlined />
                Upload a CSV file
              </a-button>
            </a-upload>
            <p class="mt-4 text-sm">
              Max. of 200 recipients, and must have a column with a heading called "email".
            </p>
            <a :href="sampleFilePath">
              <p class="mt-4 mb-2 cursor-pointer text-sm">
                Download a sample csv
                <DownloadOutlined />
              </p>
            </a>
          </a-flex>
          <a-flex v-else-if="uploadError" vertical>
            <p class="text-rumi-orange-800">
              {{ uploadError }}
            </p>
            <br />
            <AButton class="marginTop-std" @click="retryUpload">
              <template #icon>
                <RedoOutlined />
              </template>
              Retry
            </AButton>
          </a-flex>
          <a-flex v-else-if="fileList && fileList.length > 0" vertical>
            <p>Successfully imported {{ fileList[0].name }}</p>
          </a-flex>
        </a-flex>

        <a-divider />

        <a-flex>
          <a-flex vertical class="b-1 b-solid b-rumi-grey-400 w-full rounded p-4">
            <p class="font-500 mb-2 text-sm">Requested Data Access</p>
            <p class="text-rumi-grey-600 mb-3 text-sm">
              Select the information these organisations will be asked to share.
            </p>
            <a-checkbox-group
              v-if="requestedScopeOptions.length > 0"
              v-model:value="requestedScopes"
              :options="requestedScopeOptions"
            />
            <p v-else class="text-rumi-grey-600 mb-0 text-sm">
              No data access packages are enabled for this Vision organisation.
            </p>
          </a-flex>
        </a-flex>

        <a-divider />

        <a-flex>
          <a-flex vertical class="b-1 b-solid b-rumi-grey-400 w-full rounded p-4">
            <p class="font-500 mb-2 text-sm">Emails to Invite ({{ recipients.length }})</p>
            <p v-if="recipients.length === 0" class="mb-0 text-sm">No-one added yet...</p>
            <div v-else class="max-h-[200px] overflow-y-auto">
              <ATag
                v-for="r in recipients"
                :key="r.email"
                class="marginBottom-sm"
                closable
                @close="removeRecipient(r.email)"
              >
                {{ r.email }}
              </ATag>
            </div>
          </a-flex>
        </a-flex>

        <a-flex v-if="invalidEmails.length > 0" class="mt-2">
          <a-flex vertical class="b-1 b-solid b-rumi-grey-400 w-full rounded p-4">
            <a-flex align="middle">
              <a-flex vertical>
                <p>
                  <WarningOutlined class="mr-2 text-lg" />
                </p>
              </a-flex>
              <a-flex flex="auto">
                <p class="text-sm" style="mb-3">
                  Could not add the following emails - they are either invalid, duplicate or have
                  already been invited
                </p>
              </a-flex>
            </a-flex>
            <a-flex>
              <a-flex vertical>
                <div class="max-h-[200px] overflow-y-auto">
                  <ATag v-for="email in invalidEmails" :key="email" class="marginBottom-sm">
                    {{ email }}
                  </ATag>
                </div>
              </a-flex>
            </a-flex>
          </a-flex>
        </a-flex>

        <!-- <a-flex
          justify="end"
          class="mt-4"
        >
          <a-flex vertical>
            <p>
              <span
                v-if="voucherPool === undefined"
                class="text-rumi-orange-800"
              >
                No vouchers available for distribution.
              </span>
              <span
                v-else-if="notEnoughVouchers"
                class="text-rumi-orange-800"
              >
                Not enough vouchers available to send to every recipient.
              </span>
              <span v-else>
                {{ availableVoucherCount }} vouchers available for {{ voucherPool?.description }}.
              </span>
            </p>
          </a-flex>
        </a-flex> -->

        <!-- <label class="flex justify-end items-center w-full">
          <p class="mr-3">Include Vouchers</p>
          <ACheckbox
            v-model:checked="includeVouchers"
            :disabled="notEnoughVouchers"
          />
        </label> -->

        <label class="mt-4 flex w-full items-center justify-end">
          <p class="mr-3">Send notification email about invitation</p>
          <ACheckbox v-model:checked="sendInviteEmail" />
        </label>

        <div class="mt-6 flex justify-end">
          <a-button class="mr-4" @click="onCancel"> Cancel </a-button>
          <APopconfirm
            :title="`By proceeding you confirm that you have permission to email the recipients on behalf of ${currentOrg?.name}.`"
            ok-text="Yes"
            cancel-text="No"
            :overlay-style="{
              width: '400px',
            }"
            :disabled="!canSendInvites"
            @confirm="
              async () => {
                await submitNewInvites()
              }
            "
          >
            <a-button type="primary" :disabled="!canSendInvites">
              <sync-outlined v-if="isCreatingInvitations" spin />
              Send Invites
            </a-button>
          </APopconfirm>
        </div>
      </a-flex>
    </a-flex>
  </a-modal>
</template>
